(function(scope) {
  'use strict';
  const id = value => String(value || '').split('/').pop();
  function matches(line, scope) {
    return scope?.all === true || (scope?.productIds || []).includes(id(line.product_id)) || (scope?.variantIds || []).includes(id(line.variant_id || line.id));
  }
  function applications(cart) {
    return [...(cart?.cart_level_discount_applications || []), ...(cart?.items || []).flatMap(line => (line.line_level_discount_allocations || []).map(a => a.discount_application))].filter(Boolean);
  }
  const sameCode = (a,b) => String(a || '').toUpperCase() === String(b || '').toUpperCase();
  const lineTotal = line => line.final_line_price ?? line.line_price ?? line.price * line.quantity;
  // A blank preview cap means the merchant did not set one. A present but invalid or past cap still hides the offer.
  function previewOpen(stamp, now) {
    if (stamp == null || stamp === '') return true;
    const time = Date.parse(stamp);
    return Number.isFinite(time) && now < time;
  }
  function scheduleOpen(offer, now) {
    if (!previewOpen(offer.validUntil, now)) return false;
    if (offer.startsAt) {
      const start = Date.parse(offer.startsAt);
      if (!Number.isFinite(start) || now < start) return false;
    }
    if (offer.endsAt) {
      const end = Date.parse(offer.endsAt);
      if (!Number.isFinite(end) || now >= end) return false;
    }
    return true;
  }
  function evaluate(offer, cart, pending, country, currency, now = Date.now()) {
    if (!cart || cart.currency !== currency || !offer.countries?.includes(country)) return { visible:false };
    if (!scheduleOpen(offer, now)) return { visible:false };
    const applied = offer.type !== 'shipping_rate' && applications(cart).some(a => sameCode(a.title,offer.code || offer.sourceTitle));
    if (applied) return {visible:true,state:'applied'};
    const lines = [...cart.items, ...pending];
    if (lines.some(l => l.selling_plan_allocation || l.selling_plan)) return { visible:true, state:'unknown', reason:'Subscription eligibility is checked at checkout.' };
    if (offer.type === 'shipping_rate' && lines.some(l => l.requires_shipping === false || (offer.excludedProductIds || []).includes(id(l.product_id)))) return { visible:true, state:'unknown', reason:'Shipping for this combination is checked at checkout.' };
    if (offer.minimum.kind === 'spend' && currency !== offer.currency) return { visible:true, state:'unknown', reason:'This offer is calculated in '+offer.currency+'. Eligibility in '+currency+' is checked at checkout.' };
    const eligible = lines.filter(l=>matches(l,offer.items));
    let value = offer.minimum.kind === 'quantity' ? eligible.reduce((sum,l)=>sum+l.quantity,0) : eligible.reduce((sum,l)=>sum+(offer.type==='shipping_rate' ? (l.final_line_price ?? l.line_price ?? l.price*l.quantity) : (l.original_line_price ?? l.price*l.quantity)),0);
    // Native total_price includes order-level discounts absent from final_line_price.
    if (offer.type === 'shipping_rate' && offer.items?.all === true) value = cart.total_price + pending.reduce((sum,line)=>sum+lineTotal(line),0);
    const threshold = offer.minimum.value;
    let remaining = Math.max(0,threshold-value);
    let rewardRemaining = 0;
    if (offer.type === 'bxgy') {
      // Overlapping buy/get scopes consume separate units. Do not count the same unit twice.
      const buy = lines.filter(l=>matches(l,offer.buyItems));
      const get = lines.filter(l=>matches(l,offer.items));
      const buyQuantity = buy.reduce((sum,l)=>sum+l.quantity,0);
      const getQuantity = get.reduce((sum,l)=>sum+l.quantity,0);
      const overlapQuantity = get.filter(l=>matches(l,offer.buyItems)).reduce((sum,l)=>sum+l.quantity,0);
      value = offer.minimum.kind === 'quantity' ? buyQuantity : buy.reduce((sum,l)=>sum+(l.original_line_price ?? l.price*l.quantity),0);
      remaining = Math.max(0,threshold-value);
      if (offer.minimum.kind === 'quantity') rewardRemaining = Math.max(0,offer.getQuantity-(getQuantity-Math.max(0,threshold-(buyQuantity-overlapQuantity))));
      else rewardRemaining = offer.getQuantity; // Mixed-price spend/get allocation needs native evaluation.
    }
    const present = applications(cart).filter(a=>a.title!==(offer.code || offer.sourceTitle));
    const conflict = present.some(a => !offer.combinesWith?.[a.target_type==='shipping_line'?'shippingDiscounts':a.target_type==='line_item'?'productDiscounts':'orderDiscounts']);
    return { visible:true, state:applied?'applied':remaining || rewardRemaining?'progress':'estimated', value, threshold, remaining, rewardRemaining, conflict, scope:offer.minimum.kind };
  }
  function visibleOffers(feed, cart, pending, country, currency, now=Date.now()) {
    if (feed?.version !== 1 || !Array.isArray(feed.offers) || !previewOpen(feed.validUntil, now)) return [];
    const entries = feed.offers.map(offer=>({offer,state:evaluate(offer,cart,pending,country,currency,now)})).filter(entry=>entry.state.visible);
    const discount = entries.find(entry=>entry.offer.sourceId===feed.shippingDiscountSourceId);
    for (const entry of entries.filter(entry=>entry.offer.type==='shipping_rate' && entry.state.state!=='unknown')) {
      const {offer,state} = entry;
      if (offer.minimum.kind!=='spend' || !offer.items?.all || !Number.isSafeInteger(cart.total_price) || cart.total_price<0) {
        entry.state={visible:true,state:'unknown',reasonKey:'shipping_basis_unknown'}; continue;
      }
      const present = applications(cart);
      const otherDiscount = present.some(a=>!sameCode(a.title,discount?.offer.code));
      const unexplainedDiscount = cart.total_discount>0 && !present.length;
      if (pending.length && (otherDiscount || unexplainedDiscount)) {
        entry.state={visible:true,state:'unknown',reasonKey:'shipping_basis_unknown'}; continue;
      }
      const pendingTotal = pending.reduce((sum,line)=>sum+lineTotal(line),0);
      let value = cart.total_price + pendingTotal;
      let basis = 'native';
      let code;
      if (discount && ['applied','estimated'].includes(discount.state.state) && !discount.state.conflict) {
        const candidate=discount.offer, percentage=candidate.benefit?.percentage;
        if (!candidate.items?.all || candidate.minimum.kind!=='spend' || candidate.currency!==currency || !(percentage>0 && percentage<1) || otherDiscount || unexplainedDiscount) {
          entry.state={visible:true,state:'unknown',reasonKey:'shipping_basis_unknown'}; continue;
        }
        const applied = discount.state.state==='applied';
        if (!applied || pendingTotal>0) {
          const existingSavings = applied ? cart.total_discount : 0;
          const rate = Math.round(percentage*1000000);
          const scaled = (value+existingSavings)*rate;
          if (!Number.isSafeInteger(existingSavings) || existingSavings<0 || !Number.isSafeInteger(scaled) || Math.abs(rate/1000000-percentage)>1e-12) {
            entry.state={visible:true,state:'unknown',reasonKey:'shipping_basis_unknown'}; continue;
          }
          const additionalSavings = Math.floor(scaled/1000000)-existingSavings;
          if (additionalSavings<0) {entry.state={visible:true,state:'unknown',reasonKey:'shipping_basis_unknown'}; continue;}
          value -= additionalSavings;
        }
        basis = discount.state.state==='applied' ? 'applied' : 'projected';
        code = candidate.code;
      }
      if (!Number.isSafeInteger(value) || value<0) {entry.state={visible:true,state:'unknown',reasonKey:'shipping_basis_unknown'}; continue;}
      const remaining=Math.max(0,state.threshold-value);
      entry.state={...state,value,remaining,state:remaining?'progress':'estimated',basis,code,pending:pending.length>0};
    }
    return entries;
  }
  const api = { evaluate, visibleOffers, matches, applications };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (scope) scope.LeveretFamilyOffers = api;
})(typeof window !== 'undefined' ? window : globalThis);
